import * as R from 'ramda';
import React, { PureComponent } from 'react';
import { withNamespaces } from 'react-i18next';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import { Grid, Button, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import uuidv4 from 'uuid/v4';
import { withReduxForm, ShowHidePasswordTextField, Phone, Select, TypeSelect } from 'utilsModule/form/reduxFormMui';
import { Back } from 'utilsModule/components';
import HeaderComponent from 'appModule/main/layouts/Header';
import Logo from 'appModule/logo/logoNoText';

const styles = theme => ({
  pageContainer: {
    height: '84vh',
    width: '100vw',
    margin: 0,
    backgroundColor: '#fff',
    paddingBottom: 100
  },
  back: {
    height: '16vh',
    padding: 20,
    backgroundColor: '#fff',
  },
  banner: {
    textAlign: 'center'
  },
  title: {
    letterSpacing: '0.4px',
  },
  subTitle: {
    whiteSpace: 'pre-line',
    textAlign: 'center',
    letterSpacing: '0.5px',
    fontStretch: 'normal',
    lineHeight: 1.5,
    fontSize: '15px',
  },
  card: {
    maxWidth: 350,
    margin: '0 auto',
    borderRadius: 20,
    padding: `${theme.spacing(3)}px ${theme.spacing(2)}px`,
  },
  policy: {
    marginTop: 20,
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 1.5,
    display: 'inline-block',
  },
  link: {
    color: '#2c7df6',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  right: {
    textAlign: 'right',
    cursor: 'pointer',
  },
});

const layout = {
  header: {
    containerProps: {
      justify: 'center',
    },
    components: [
      {
        id: uuidv4(),
        name: 'banner',
        component: ({ t }) => (
          <Grid container direction="column" alignItems="center">
            <Grid item xs={12} style={{ paddingBottom: '10px' }}><Logo /></Grid>
            <Grid item xs={12} style={{ paddingBottom: '15px' }}>
              <Typography variant='h4' style={styles.title}>{t('reverifyLogin.title')}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant='body1'
                align='center'
                style={styles.subTitle}>{t('reverifyLogin.description')}</Typography>
            </Grid>
          </Grid>
        ),
        colSpans: { xs: 12 },
        inject: ({ runtimeProps: { t } }) => ({ t }),
      },
    ],
  },
  body: {
    itemProps: {
      style: {
        marginTop: 10,
      },
    },
    sections: [
      {
        id: uuidv4(),
        name: 'loginInfo',
        containerProps: {
          overall: {
            spacing: 0,
          },
          body: {
            spacing: 0,
          },
        },
        rows: [
          {
            id: uuidv4(),
            cols: [
              {
                name: 'mobile',
                label: 'Mobile Number',
                component: Phone,
                colSpans: { xs: 8 },
                required: true,
                inject: ({ runtimeProps: { t } }) => ({ label: t('login.mobileNo') }),
              },
            ],
          },
          {
            id: uuidv4(),
            cols: [
              {
                name: 'encryptPassword',
                label: 'Password',
                colSpans: { xs: 8 },
                component: ShowHidePasswordTextField,
                required: true,
                inject: ({ runtimeProps: { t } }) => ({ label: t('login.password') }),
              },
            ],
          },
        ],
      },
    ],
  },
  footer: {
    itemProps: {
      style: {
        marginTop: 20,
        alignItems: 'center'
      },
    },
    components: [
      {
        id: uuidv4(),
        component: Button,
        type: 'submit',
        children: 'Verify Log In',
        variant: 'contained',
        color: 'primary',
        style: { width: '40%', left: '28%' },
        className: 'milo-btn-submit-medium',
        colSpans: { xs: 12 },
        inject: ({ submitting, runtimeProps: { t } }) => ({ disabled: submitting, children: t('common:buttonLabels.login') }),
      },
    ],
  },
};

const fieldGroups = R.pipe(
  R.map(({ name, rows }) => ({
    name: name || 'root',
    fields: R.flatten(R.map(R.prop('cols'), rows)),
  })),
  R.reduceBy((acc, { fields }) => acc.concat(fields), [], R.prop('name')),
  R.unless(R.prop('root'), R.assoc('root', [])),
)(layout.body.sections);

const validate = (values, props) => {
  const errors = {};
  const { runtimeProps: { t } } = props;

  const getFieldValue = (groupName, fieldName) => R.prop(fieldName, groupName === 'root' ? values : values[groupName]);
  const setFieldError = (groupName, fieldName, error) => {
    (groupName === 'root' ? errors : (errors[groupName] = errors[groupName] || {}))[fieldName] = error;
  };
  const attachValueToField = (groupName) => (field) =>
    R.assoc(
      'value',
      R.contains(field.component, [Select, TypeSelect])
        ? R.prop('value', getFieldValue(groupName, field.name))
        : getFieldValue(groupName, field.name),
      field,
    );
  const fieldGroupsWithValue = R.mapObjIndexed(
    (groupFields, groupName) => R.map(attachValueToField(groupName), groupFields),
    fieldGroups,
  );
  const requiredFieldGroupsWithValue = R.map(
    R.filter(({ name, required }) => required),
    fieldGroupsWithValue,
  );

  const requiredPhoneFieldGroupsWithValue = R.map(
    R.filter(R.pipe(R.prop('component'), R.identical(Phone))),
    requiredFieldGroupsWithValue,
  );

  /* Validate Phone */
  R.mapObjIndexed(
    (groupFields, groupName) =>
      R.map(({ name, value: { countryCode: { value: countryCode } = {}, value } = {} }) => {
        !countryCode && (errors[groupName] = R.assocPath([name, 'countryCode'], 'Oops', errors[groupName]));
        !value && (errors[groupName] = R.assocPath([name, 'value'], t('validation:Please enter Mobile No'), errors[groupName]));
      }, groupFields),
    requiredPhoneFieldGroupsWithValue,
  );

  /* Validate Required Fields */
  R.mapObjIndexed(
    (groupFields, groupName) =>
      R.map(
        ({ name, component, label, value }) => !value && setFieldError(groupName, name, t('validation:Please enter Password')),
        groupFields,
      ),
    requiredFieldGroupsWithValue,
  );

  return errors;
};

const config = {
  form: 'login',
  validate,
};
@withNamespaces('auth', { wait: true })
@withRouter
@withStyles(styles, { index: 1 })
@withReduxForm({ layout, config })
class ReverifyLogin extends PureComponent {
  static propTypes = {
    /* container */
    signIn: PropTypes.func.isRequired,
    forgetPassword: PropTypes.func.isRequired,
    /* withStyles */
    classes: PropTypes.object,
    /* createReduxForm */
    Form: PropTypes.func.isRequired,
  };

  render() {
    const {
      classes, Form, initialValues, runtimeProps, signIn, t, forgetPassword, isOnBoard
    } = this.props;

    return (
      <React.Fragment>
        <HeaderComponent isOnBoard={isOnBoard} />
        <div style={{ paddingTop: 50 }}>
          <Back className={classes.back} label={t('common:buttonLabels.login')} backToWebsite={true} path='/auth' />
          <Grid container className={classes.pageContainer} alignItems='center'>
            <Grid item xs />
            <Grid item xs={5}>
              <Form
                autocomplete='off'
                runtimeProps={{
                  ...runtimeProps,
                  t,
                  forgetPassword,
                  classes,
                }}
                initialValues={initialValues}
                className={classes.formContainer}
                onSubmit={signIn}
                sections={[]}
              />
            </Grid>
            <Grid item xs />
          </Grid>
        </div>
      </React.Fragment>
    );

  }
}

export default ReverifyLogin;
